/**
 * Task Guide Context - Thin wrapper over React Query hooks.
 * Exposes progress and dismiss actions to the sidebar widget.
 *
 * All evaluation happens on the backend.
 */

/* eslint-disable react-refresh/only-export-components */

import { createContext, useMemo, useCallback, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { useAuthContext } from '../hooks/useAuthContext';
import { useCurrentOrg } from '../hooks/useCurrentOrg';
import { useTaskGuideTasks, useDismissTask } from '../hooks/useTaskGuide';
import { queryKeys } from '../lib/query/client';
import type { TaskGuideProgress, TaskGuideContextType } from '../types/taskGuide';

export const TaskGuideContext = createContext<TaskGuideContextType | undefined>(undefined);

interface TaskGuideProviderProps {
  children: ReactNode;
}

export function TaskGuideProvider({ children }: TaskGuideProviderProps) {
  const { user, isAuthenticated } = useAuthContext();
  const { currentOrg } = useCurrentOrg();
  const queryClient = useQueryClient();

  const orgId = currentOrg?.id;
  const enabled = isAuthenticated && !!user;

  const { data, isLoading } = useTaskGuideTasks(orgId, enabled);
  const dismiss = useDismissTask(orgId);

  const tasks = useMemo(() => data?.tasks ?? [], [data?.tasks]);

  const visibleTasks = useMemo(() => tasks.filter((t) => t.visible && !t.dismissed), [tasks]);

  const progress: TaskGuideProgress = useMemo(() => {
    const completed = visibleTasks.filter((t) => t.completed).length;
    const total = visibleTasks.length;
    return {
      completed,
      total,
      percentage: total > 0 ? Math.round((completed / total) * 100) : 0,
    };
  }, [visibleTasks]);

  const dismissTask = useCallback(
    async (taskId: string) => {
      await dismiss.mutateAsync(taskId);
    },
    [dismiss],
  );

  const refresh = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: queryKeys.taskGuideTasks(orgId) });
  }, [queryClient, orgId]);

  const contextValue: TaskGuideContextType = {
    tasks,
    visibleTasks,
    progress,
    loading: isLoading,
    dismissTask,
    refresh,
  };

  return <TaskGuideContext.Provider value={contextValue}>{children}</TaskGuideContext.Provider>;
}
